import { useEffect, useState } from "react";
import api, { fmtINRFull, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Edit2 } from "lucide-react";
import ExcelImport from "@/components/ExcelImport";

export default function SORMaster() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sor", { params: { search: q, limit: 500 } }); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">SOR Master</h1>
          <p className="mt-1.5 text-slate-500">Central schedule of rates. {items.length} items shown.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelImport
            mode="sor"
            buttonLabel="Import Excel"
            onImport={async (items, _replace) => {
              const { data } = await api.post("/sor/import", { items, replace: false });
              await load();
              return data;
            }}
          />
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-sor-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E]"><Plus className="h-4 w-4 mr-1.5" /> New Item</Button>
            </DialogTrigger>
            <SORDialog onDone={() => { setOpenNew(false); load(); }} />
          </Dialog>
        </div>
      </div>
      <div className="relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="sor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search IOW code or item…" className="pl-9 h-10" />
      </div>
      <Card className="border-slate-200 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
          <div className="col-span-2">IOW</div>
          <div className="col-span-6">Item</div>
          <div className="col-span-1">UOM</div>
          <div className="col-span-2 text-right">Rate</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? <div className="p-8 text-center text-slate-500 text-sm">Loading…</div> : items.length === 0 ? (
          <div data-testid="sor-empty" className="p-8 text-center text-slate-500 text-sm">No items.</div>
        ) : items.map((s) => (
          <div key={s.iow_code} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-100 items-center text-sm hover:bg-slate-50/50">
            <div className="col-span-2 font-mono text-xs text-slate-700">{s.iow_code}</div>
            <div className="col-span-6 text-slate-800 truncate" title={s.item_of_work}>{s.item_of_work}</div>
            <div className="col-span-1 text-xs text-slate-500">{s.uom}</div>
            <div className="col-span-2 text-right font-semibold text-[#0D3B66]">{fmtINRFull(s.rate)}</div>
            <div className="col-span-1 text-right">
              <button data-testid={`sor-edit-${s.iow_code}`} onClick={() => setEditItem(s)} className="text-slate-400 hover:text-[#0D3B66]">
                <Edit2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        {editItem && <SORDialog initial={editItem} onDone={() => { setEditItem(null); load(); }} />}
      </Dialog>
    </div>
  );
}

function SORDialog({ initial, onDone }) {
  const [iow, setIow] = useState(initial?.iow_code || "");
  const [name, setName] = useState(initial?.item_of_work || "");
  const [uom, setUom] = useState(initial?.uom || "");
  const [rate, setRate] = useState(initial?.rate ?? "");
  const [category, setCategory] = useState(initial?.category || "");
  const [subCategory, setSubCategory] = useState(initial?.sub_category || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { iow_code: iow, item_of_work: name, uom, rate: parseFloat(rate) || 0, category, sub_category: subCategory };
      if (initial) await api.patch(`/sor/${initial.iow_code}`, body);
      else await api.post("/sor", body);
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent data-testid="sor-dialog">
      <DialogHeader><DialogTitle>{initial ? "Edit SOR item" : "New SOR item"}</DialogTitle></DialogHeader>
      <div className="space-y-4 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>IOW Code</Label><Input data-testid="sor-iow" value={iow} onChange={(e) => setIow(e.target.value)} disabled={!!initial} /></div>
          <div className="space-y-1.5"><Label>UOM</Label><Input data-testid="sor-uom" value={uom} onChange={(e) => setUom(e.target.value)} /></div>
        </div>
        <div className="space-y-1.5"><Label>Item of Work</Label><Input data-testid="sor-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Category</Label><Input data-testid="sor-cat" value={category} onChange={(e) => setCategory(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Sub-category</Label><Input data-testid="sor-subcat" value={subCategory} onChange={(e) => setSubCategory(e.target.value)} /></div>
        </div>
        <div className="space-y-1.5"><Label>Rate (₹)</Label><Input data-testid="sor-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
        {err && <div className="text-sm text-rose-700">{err}</div>}
      </div>
      <DialogFooter>
        <Button data-testid="sor-save" onClick={save} disabled={busy || !iow || !name} className="bg-[#0D3B66] hover:bg-[#114B82]">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
